/**
 * DashboardDataContext — F1: Dashboard Shared Data Layer
 *
 * Fetches pre-aggregated data from dedicated dashboard endpoints. Widgets
 * consume this context rather than making their own independent API calls.
 *
 * PRD §7.2 — replaces the bulk `fetchProjects('dashboard', …, { statusId: 2 })`
 * call (~3,700 full Project objects) with ~8-10 targeted, pre-aggregated
 * endpoint calls that fire in parallel.
 *
 * Usage:
 *   Wrap the DashboardPage with <DashboardDataProvider>.
 *   Widgets call useDashboardData() to access shared state.
 */

import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Project, WIPReport, UtilisationData } from '@/types';
import type { RevenueRecognition } from '@/types/budget.types';
import {
  fetchProjects,
  fetchWIPReports,
  fetchUtilisationData,
  fetchExecutiveKpis,
  fetchFinanceKpis,
  fetchAtRiskProjects,
  fetchDashboardRevenueRecognition,
  fetchClientConcentration,
  fetchPortfolioProfitability,
  fetchUpcomingKeyDates,
  fetchRevenueCostBurnByProject,

  getDataMode,
} from '@/lib/data-service';
import type {
  ExecutiveKpis,
  FinanceKpis,
  AtRiskProject,
  DashboardRevenueRecognition,
  ClientConcentrationData,
  PortfolioProfitabilityData,
  KeyDateEvent,
  RevenueCostBurnProject,
  ProjectFinancialSummaryRow,
} from '@/lib/data-service';
import { getBudgetDataByProjectId } from '@/lib/mock-data/budget-mock-data';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@/providers/AuthProvider';
import { usePermissions } from '@/contexts/PermissionsContext';

// ── Per-project revenue recognition shape consumed by Finance widgets ─────────
export interface ProjectRevenueRecognition {
  projectId: number;
  projectName: string;
  clientName: string;
  method: RevenueRecognition['method'];
  budgetRevenue: number;
  completionPercent: number;
  revenueRecognised: number;
  revenueToRecognise: number;
  deferredRevenue: number;
  unbilledRevenue: number;
}

// ── Context shape ─────────────────────────────────────────────────────────────
export interface DashboardDataContextValue {
  // Pre-computed aggregates (replaces raw Project[])
  execKpis: ExecutiveKpis | null;
  finKpis: FinanceKpis | null;
  atRiskProjects: AtRiskProject[];
  revenueRecognitions: DashboardRevenueRecognition[];
  profitability: PortfolioProfitabilityData | null;
  keyDates: KeyDateEvent[];
  clientConcentration: ClientConcentrationData | null;
  revenueCostBurnByProject: RevenueCostBurnProject[];

  // Lightweight per-project rows from SQL (live mode)
  projectFinancialSummary: ProjectFinancialSummaryRow[];

  // Kept as-is (still fetched directly)
  wipReports: WIPReport[];
  utilisationData: UtilisationData[];

  // Backward compatibility: demo/tour mode still provides these
  projects: Project[];
  activeProjects: Project[];
  /** @deprecated Use revenueRecognitions from dedicated endpoint in live mode */
  legacyRevenueRecognitions: ProjectRevenueRecognition[];

  // Granular loading states for progressive rendering
  kpisLoading: boolean;
  atRiskLoading: boolean;
  profitabilityLoading: boolean;
  revenueRecLoading: boolean;
  keyDatesLoading: boolean;
  clientConcLoading: boolean;
  rcbByProjectLoading: boolean;
  wipLoading: boolean;
  utilisationLoading: boolean;

  // Per-project data loading (live mode fetches projects in the background)
  projectsDataLoading: boolean;

  // Backward compatibility aliases
  projectsLoading: boolean;

  // Composite
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;

  /** Manually trigger a full data refresh. */
  refresh: () => Promise<void>;

  /** Timestamp of the most recent successful fetch (ms since epoch). */
  lastFetchedAt: number | null;
}

const DashboardDataContext = createContext<DashboardDataContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────
export function DashboardDataProvider({ children }: { children: ReactNode }) {
  const { currentCompany } = useCompany();
  const { authenticatedUser } = useAuth();
  const { reports } = usePermissions();

  // Derive managerId for PM-persona scoping: PMs who are NOT executives
  // should only see their own projects in revenue-recognition data.
  const revenueRecManagerId = (!reports.executiveDashboard && reports.pmDashboard && authenticatedUser?.id)
    ? parseInt(authenticatedUser.id, 10)
    : undefined;

  // Pre-computed aggregates from dedicated endpoints
  const [execKpis, setExecKpis] = useState<ExecutiveKpis | null>(null);
  const [finKpis, setFinKpis] = useState<FinanceKpis | null>(null);
  const [atRiskProjectsData, setAtRiskProjectsData] = useState<AtRiskProject[]>([]);
  const [revenueRecognitions, setRevenueRecognitions] = useState<DashboardRevenueRecognition[]>([]);
  const [profitability, setProfitability] = useState<PortfolioProfitabilityData | null>(null);
  const [keyDates, setKeyDates] = useState<KeyDateEvent[]>([]);
  const [clientConcentration, setClientConcentration] = useState<ClientConcentrationData | null>(null);
  const [revenueCostBurnByProject, setRevenueCostBurnByProject] = useState<RevenueCostBurnProject[]>([]);
  // projectFinancialSummary is now fetched on-demand per page by usePagedFinancialSummary hook.
  // Kept as empty array for backward-compatibility with any consumer that still reads it.
  const projectFinancialSummary: ProjectFinancialSummaryRow[] = [];

  // Kept as-is
  const [wipReports, setWipReports] = useState<WIPReport[]>([]);
  const [utilisationData, setUtilisationData] = useState<UtilisationData[]>([]);

  // Backward compatibility: demo/tour mode project arrays
  const [projects, setProjects] = useState<Project[]>([]);
  const [legacyRevenueRecognitions, setLegacyRevenueRecognitions] = useState<ProjectRevenueRecognition[]>([]);

  // Granular loading states
  const [kpisLoading, setKpisLoading] = useState(true);
  const [atRiskLoading, setAtRiskLoading] = useState(true);
  const [profitabilityLoading, setProfitabilityLoading] = useState(true);
  const [revenueRecLoading, setRevenueRecLoading] = useState(true);
  const [keyDatesLoading, setKeyDatesLoading] = useState(true);
  const [clientConcLoading, setClientConcLoading] = useState(true);
  const [rcbByProjectLoading, setRcbByProjectLoading] = useState(true);
  const [wipLoading, setWipLoading] = useState(true);
  const [utilisationLoading, setUtilisationLoading] = useState(true);
  const [projectsDataLoading, setProjectsDataLoading] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchAll = async (isInitial: boolean) => {
    const mode = getDataMode();
    const isLive = mode === 'live';

    if (isInitial) {
      setKpisLoading(true);
      setAtRiskLoading(true);
      setProfitabilityLoading(true);
      setRevenueRecLoading(true);
      setKeyDatesLoading(true);
      setClientConcLoading(true);
      setRcbByProjectLoading(true);
      setWipLoading(true);
      setUtilisationLoading(true);
      setProjectsDataLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    const promises: Promise<void>[] = [];

    if (isLive) {
      // ── Live mode: use dedicated pre-aggregated endpoints ──────────────
      promises.push(
        fetchExecutiveKpis()
          .then(data => { if (isMountedRef.current) setExecKpis(data); })
          .catch(err => { console.error('[DashboardData] exec kpis:', err); })
          .finally(() => { if (isMountedRef.current) setKpisLoading(false); }),
      );

      promises.push(
        fetchFinanceKpis()
          .then(data => { if (isMountedRef.current) setFinKpis(data); })
          .catch(err => { console.error('[DashboardData] fin kpis:', err); })
          .finally(() => { if (isMountedRef.current) setKpisLoading(false); }),
      );

      promises.push(
        fetchAtRiskProjects()
          .then(data => { if (isMountedRef.current) setAtRiskProjectsData(data); })
          .catch(err => { console.error('[DashboardData] at-risk:', err); })
          .finally(() => { if (isMountedRef.current) setAtRiskLoading(false); }),
      );

      promises.push(
        fetchDashboardRevenueRecognition(revenueRecManagerId)
          .then(data => { if (isMountedRef.current) setRevenueRecognitions(data); })
          .catch(err => { console.error('[DashboardData] rev rec:', err); })
          .finally(() => { if (isMountedRef.current) setRevenueRecLoading(false); }),
      );

      promises.push(
        fetchPortfolioProfitability()
          .then(data => { if (isMountedRef.current) setProfitability(data); })
          .catch(err => { console.error('[DashboardData] profitability:', err); })
          .finally(() => { if (isMountedRef.current) setProfitabilityLoading(false); }),
      );

      promises.push(
        fetchUpcomingKeyDates()
          .then(data => { if (isMountedRef.current) setKeyDates(data); })
          .catch(err => { console.error('[DashboardData] key dates:', err); })
          .finally(() => { if (isMountedRef.current) setKeyDatesLoading(false); }),
      );

      promises.push(
        fetchClientConcentration()
          .then(data => { if (isMountedRef.current) setClientConcentration(data); })
          .catch(err => { console.error('[DashboardData] client conc:', err); })
          .finally(() => { if (isMountedRef.current) setClientConcLoading(false); }),
      );

      promises.push(
        fetchRevenueCostBurnByProject()
          .then(data => { if (isMountedRef.current) setRevenueCostBurnByProject(data); })
          .catch(err => { console.error('[DashboardData] rcb by project:', err); })
          .finally(() => { if (isMountedRef.current) setRcbByProjectLoading(false); }),
      );

      // Project financial summary is now fetched on-demand per page by the
      // usePagedFinancialSummary hook. Just mark loading as complete.
      if (isMountedRef.current) setProjectsDataLoading(false);
    } else {
      // ── Demo/tour mode: keep the old fetchProjects path for mock data ──
      promises.push(
        fetchProjects('dashboard', undefined, { statusId: 2 })
          .then(val => {
            if (!isMountedRef.current) return;
            const allProjects: Project[] = Array.isArray(val) ? val : (val?.data ?? []);
            setProjects(allProjects);
            setLegacyRevenueRecognitions(deriveRevenueRecognitions(allProjects));
          })
          .catch(err => {
            if (!isMountedRef.current) return;
            console.error('[DashboardData] Failed to fetch projects:', err);
            setProjects([]);
            setLegacyRevenueRecognitions([]);
          })
          .finally(() => {
            if (!isMountedRef.current) return;
            setKpisLoading(false);
            setAtRiskLoading(false);
            setProfitabilityLoading(false);
            setRevenueRecLoading(false);
            setKeyDatesLoading(false);
            setClientConcLoading(false);
            setRcbByProjectLoading(false);
            setProjectsDataLoading(false);
          }),
      );
    }

    // WIP and utilisation are still fetched from their existing endpoints
    promises.push(
      fetchWIPReports('dashboard')
        .then(val => {
          if (!isMountedRef.current) return;
          const wip: WIPReport[] = !val ? [] : Array.isArray(val) ? val : ('data' in val ? val.data : []);
          setWipReports(wip);
        })
        .catch(err => {
          if (!isMountedRef.current) return;
          console.error('[DashboardData] Failed to fetch WIP reports:', err);
          setWipReports([]);
        })
        .finally(() => {
          if (isMountedRef.current) setWipLoading(false);
        }),
    );

    promises.push(
      fetchUtilisationData('dashboard')
        .then(val => {
          if (!isMountedRef.current) return;
          setUtilisationData(val ?? []);
        })
        .catch(err => {
          if (!isMountedRef.current) return;
          console.error('[DashboardData] Failed to fetch utilisation:', err);
          setUtilisationData([]);
        })
        .finally(() => {
          if (isMountedRef.current) setUtilisationLoading(false);
        }),
    );

    try {
      await Promise.allSettled(promises);
    } catch (err) {
      if (!isMountedRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load dashboard data';
      console.error('[DashboardData] Unexpected error:', err);
      setError(message);
    }

    if (isMountedRef.current) {
      setLastFetchedAt(Date.now());
      setIsRefreshing(false);
    }
  };

  // Fetch once a company is selected and whenever it changes.
  // Also re-fetch when revenueRecManagerId changes (persona/user change).
  useEffect(() => {
    if (!currentCompany?.id) return;
    void fetchAll(true);
  }, [currentCompany?.id, revenueRecManagerId]);

  const refresh = async () => {
    await fetchAll(false);
  };

  // Derived: active projects (demo/tour mode backward compat)
  const activeProjects = projects.filter(p => p.statusId === 2);

  // Composite loading flags — projectsLoading is aliased for backward compat
  const projectsLoading = kpisLoading;
  const isLoading = kpisLoading || wipLoading || utilisationLoading;

  return (
    <DashboardDataContext.Provider
      value={{
        execKpis,
        finKpis,
        atRiskProjects: atRiskProjectsData,
        revenueRecognitions,
        profitability,
        keyDates,
        clientConcentration,
        revenueCostBurnByProject,
        projectFinancialSummary,
        wipReports,
        utilisationData,
        projects,
        activeProjects,
        legacyRevenueRecognitions,
        kpisLoading,
        atRiskLoading,
        profitabilityLoading,
        revenueRecLoading,
        keyDatesLoading,
        clientConcLoading,
        rcbByProjectLoading,
        wipLoading,
        utilisationLoading,
        projectsDataLoading,
        projectsLoading,
        isLoading,
        isRefreshing,
        error,
        refresh,
        lastFetchedAt,
      }}
    >
      {children}
    </DashboardDataContext.Provider>
  );
}

// ── Consumer hook ─────────────────────────────────────────────────────────────
export function useDashboardData(): DashboardDataContextValue {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    throw new Error('useDashboardData must be used within a DashboardDataProvider');
  }
  return ctx;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Derive per-project RevenueRecognition from budget mock data (demo mode) or
 * project fields (live mode).  Kept for demo/tour backward compatibility.
 */
function deriveRevenueRecognitions(projects: Project[]): ProjectRevenueRecognition[] {
  const mode = getDataMode();

  return projects.map(project => {
    if (mode === 'demo' || mode === 'tour') {
      const budgetData = getBudgetDataByProjectId(project.id);
      const rr = budgetData?.salesBudget?.revenueRecognition;

      if (rr) {
        return {
          projectId: project.id,
          projectName: project.name,
          clientName: project.clientNames ?? 'Unknown',
          method: rr.method,
          budgetRevenue: project.budgetRevenue ?? 0,
          completionPercent: rr.percentComplete ?? project.completionPercent ?? 0,
          revenueRecognised: rr.revenueRecognised ?? rr.recognised ?? 0,
          revenueToRecognise: rr.revenueToRecognise ?? rr.toRecognise ?? 0,
          deferredRevenue: rr.deferredRevenue ?? rr.deferred ?? 0,
          unbilledRevenue: rr.unbilledRevenue ?? rr.unbilled ?? 0,
        };
      }
    }

    const completionPct = project.completionPercent ?? 0;
    const budgetRevenue = project.budgetRevenue ?? 0;
    const actualRevenue = project.actualRevenue ?? 0;
    const revenueRecognised = actualRevenue;
    const revenueToRecognise = Math.max(0, (completionPct / 100) * budgetRevenue - actualRevenue);
    const deferredRevenue = Math.max(0, actualRevenue - (completionPct / 100) * budgetRevenue);
    const unbilledRevenue = project.wipAmount ?? 0;

    return {
      projectId: project.id,
      projectName: project.name,
      clientName: project.clientNames ?? 'Unknown',
      method: (project.revenueRecognitionMethod ?? 'percentage-of-completion') as RevenueRecognition['method'],
      budgetRevenue,
      completionPercent: completionPct,
      revenueRecognised,
      revenueToRecognise,
      deferredRevenue,
      unbilledRevenue,
    };
  });
}
